import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Product, Cart, CartItem } from '../types';

interface CartState {
  cart: Cart;
}

type CartAction =
  | { type: 'ADD_TO_CART'; payload: { product: Product; quantity: number; selectedSize?: string } }
  | { type: 'REMOVE_FROM_CART'; payload: { productId: string; selectedSize?: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { productId: string; quantity: number; selectedSize?: string } }
  | { type: 'CLEAR_CART' };

interface CartContextType extends CartState {
  addToCart: (product: Product, quantity?: number, selectedSize?: string) => void;
  removeFromCart: (productId: string, selectedSize?: string) => void;
  updateQuantity: (productId: string, quantity: number, selectedSize?: string) => void;
  clearCart: () => void;
  getItemQuantity: (productId: string) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const { product, quantity, selectedSize } = action.payload;
      const existingItemIndex = state.cart.items.findIndex(
        item => item.product.id === product.id && item.selectedSize === selectedSize
      );

      let newItems: CartItem[];
      
      if (existingItemIndex >= 0) {
        // Update existing item
        newItems = state.cart.items.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item
        newItems = [...state.cart.items, { product, quantity, selectedSize }];
      }

      const total = newItems.reduce(
        (sum, item) => sum + (item.product.price || 0) * item.quantity,
        0
      );
      const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        cart: {
          items: newItems,
          total,
          itemCount
        }
      };
    }

    case 'REMOVE_FROM_CART': {
      const newItems = state.cart.items.filter(
        item => !(item.product.id === action.payload.productId && item.selectedSize === action.payload.selectedSize)
      );

      const total = newItems.reduce(
        (sum, item) => sum + (item.product.price || 0) * item.quantity,
        0
      );
      const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        cart: {
          items: newItems,
          total,
          itemCount
        }
      };
    }

    case 'UPDATE_QUANTITY': {
      const { productId, quantity, selectedSize } = action.payload;
      
      if (quantity <= 0) {
        return cartReducer(state, { type: 'REMOVE_FROM_CART', payload: { productId, selectedSize } });
      }

      const newItems = state.cart.items.map(item =>
        item.product.id === productId && item.selectedSize === selectedSize
          ? { ...item, quantity }
          : item
      );

      const total = newItems.reduce(
        (sum, item) => sum + (item.product.price || 0) * item.quantity,
        0
      );
      const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        cart: {
          items: newItems,
          total,
          itemCount
        }
      };
    }

    case 'CLEAR_CART':
      return {
        cart: {
          items: [],
          total: 0,
          itemCount: 0
        }
      };

    default:
      return state;
  }
};

const initialState: CartState = {
  cart: {
    items: [],
    total: 0,
    itemCount: 0
  }
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  const addToCart = (product: Product, quantity = 1, selectedSize?: string) => {
    dispatch({ type: 'ADD_TO_CART', payload: { product, quantity, selectedSize } });
  };

  const removeFromCart = (productId: string, selectedSize?: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: { productId, selectedSize } });
  };

  const updateQuantity = (productId: string, quantity: number, selectedSize?: string) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, quantity, selectedSize } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getItemQuantity = (productId: string): number => {
    const item = state.cart.items.find(item => item.product.id === productId);
    return item ? item.quantity : 0;
  };

  return (
    <CartContext.Provider
      value={{
        ...state,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getItemQuantity
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
